import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {GraphMap, GraphNodeLink} from '../../api/interfaces/graph-node.interface';
import {PackageService} from '../../api/services/package.service';
import {SidebarFormAbstractComponent} from '../abstract/sidebar-form-abstract.component';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DefaultValues, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {orderBy, set} from 'lodash';
import {Subscription} from 'rxjs';
import {undoRedoStateChanged$} from '../../services/undo-redo/undo-redo-manager.service';
import {CommonModule} from '@angular/common';
import {MetaDataControlComponent} from '../../shared/forms/key-value-table/meta-data-control.component';
import {MatListModule} from '@angular/material/list';
import {SelectInputComponent} from '../../shared/forms/select-input/select-input.component';
import {MatButtonModule} from '@angular/material/button';
import {HasModuleDirective} from '../../packages/has-module.directive';
import {LinkStyleComponent} from './sections/link-style.component';
import {
  TitleDescriptionInputComponent
} from '../../shared/forms/title-description-input/title-description-input.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {
  MaporiumAccordionItemComponent
} from '../../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CommentsComponent} from '../../shared/components/comments/comments.component';
import {
  hasAddedNewProperty,
  lastMultiSelectionMetadataIds,
  undoRedoJustHappened,
  updateInProgress
} from '../../shared/helpers/signal.helpers';
import {TranslatePipe} from '@ngx-translate/core';
import {HasPackageDirective} from '../../packages/has-package.directive';
import {DomUtils} from '../../shared/helpers/dom.utils';
import {MapService} from '../../api/services/map.service';
import {isInIframe} from '../../shared/helpers/maporium.validators';
import {HasMapPermissionDirective} from '../../packages/has-map-permission.directive';
import {CytoscapeService} from '../../services/cy-services/cytoscape.service';
import {DashboardComponent} from '../../dashboard/dashboard.component';
import {TourMatMenuModule} from 'ngx-ui-tour-md-menu';
import {systemMetadata, visibleMetadata} from '../../shared/helpers/system-metadata.helpers';
import {BOTTLENECK_KEY, BottleneckMarkerComponent} from '../bottleneck-marker/bottleneck-marker.component';
import {MetaData} from '../../api/interfaces/metadata';
import {CommandFactoryService} from '../../services/undo-redo/command-factory.service';
import {FocusSessionService} from '../../services/undo-redo/focus-session.service';
import {UndoRedoManagerService} from '../../services/undo-redo/undo-redo-manager.service';

@Component({
  selector: 'maporium-link-form',
  templateUrl: './link-form.component.html',
  styleUrls: ['./link-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MetaDataControlComponent,
    MatListModule,
    SelectInputComponent,
    MatButtonModule,
    HasModuleDirective,
    LinkStyleComponent,
    TitleDescriptionInputComponent,
    CdkAccordion,
    MaporiumAccordionItemComponent,
    CommentsComponent,
    TranslatePipe,
    HasPackageDirective,
    HasMapPermissionDirective,
    TourMatMenuModule,
    BottleneckMarkerComponent
  ]
})
export class LinkFormComponent extends SidebarFormAbstractComponent<ExtendedGraphNodeLink> implements OnInit, OnChanges, OnDestroy {
  formGroup: FormGroup = new FormGroup({
    name: new FormControl(''),
    description: new FormControl(''),
    label: new FormControl(DefaultValues.link.label),
    weight: new FormControl(DefaultValues.link.weight.toString()),
    type: new FormControl(DefaultValues.link.type),
    shape: new FormControl(DefaultValues.link.shape),
    textFontSize: new FormControl(DefaultValues.link.textFontSize.toString()),
    color: new FormControl(DefaultValues.link.color),
    metadata: new FormControl([]),
    excludedFromState: new FormControl('false'),
    direction: new FormControl(DefaultValues.link.direction),
    edgeArrowType: new FormControl(DefaultValues.link.edgeArrowType),
    soundUrl: new FormControl(DefaultValues.link.soundUrl),
    labelSlope: new FormControl(DefaultValues.link.labelSlope),
    linkLabelPosition: new FormControl(0),
    linkLabelOffset: new FormControl(DefaultValues.link.linkLabelOffset)
  });

  @Input() isDefaultState = false;
  @Input() excludeFromStateOptions: { key: string, value: string, icon?: string }[] = [];
  @Input() focusedLink: ExtendedGraphNodeLink | undefined;
  @Output() resetEvent = new EventEmitter<{ link: ExtendedGraphNodeLink }>();
  @Output() updateLinkEvent = new EventEmitter<Partial<ExtendedGraphNodeLink>>();
  @Output() extendMeta = new EventEmitter<void>();
  @Output() styleTouched = new EventEmitter<{ id?: string; path: string; value: unknown }>();

  @ViewChild('linkNameInput') linkNameInput!: ElementRef;
  commentsCount = 0;
  public sectionsCount = 0;

  public currentMap: GraphMap;
  protected readonly isInIframe = isInIframe;
  private undoRedoSubscription?: Subscription;
  private styleTouchSubscription?: Subscription;

  ngOnChanges(changes: SimpleChanges) {
    // we should update the form if the focusedLink changes
    if (changes['focusedLink'] && changes['focusedLink'].currentValue) {
      const link = changes['focusedLink'].currentValue as ExtendedGraphNodeLink;
      const idChanged = changes['focusedLink'].previousValue?.id !== link.id;
      if (idChanged) {
        hasAddedNewProperty.set(false);
      }
      // MAP-1543: don't re-patch the whole form (which resets imageUrl/soundUrl/
      // name/description and jumps the caret) while the user is actively editing
      // this same link. A stale re-emit of focusedLink mid-edit would otherwise
      // clobber the field. Still patch when a different link is selected; undo/
      // redo is handled by its own subscription above.
      const isEditingThisLink = !idChanged
        && !undoRedoJustHappened()
        && this.el.nativeElement.contains(document.activeElement);
      if (!isEditingThisLink) {
        this.patchForm(link);
      }
      if (link.isNew) {
        this.selectNameInput();
      }
    }
    // check if the new value of node has isNew property and if it does, we should select the name input
    if (changes['focusedNode'] && changes['focusedNode'].currentValue?.isNew) {
      if (this.focusedLink?.isNew) {
        this.selectNameInput();
      }
    }
    setTimeout(() => {
      this.sectionsCount = DomUtils.getElementCount('linkStyleSection');
    }, 200);
  }

  ngOnInit(): void {
    super.init();
    this.styleTouchSubscription = this.watchStyleTouches((path, value) =>
      this.styleTouched.emit({id: this.focusedLink?.id, path, value}),
    );

    // Subscribe to undo/redo events to force-sync the form with current cytoscape state
    this.undoRedoSubscription = undoRedoStateChanged$.subscribe(() => {
      // Only sync when actual undo/redo happened, not when new commands are pushed
      if (!undoRedoJustHappened()) return;

      const linkId = this.focusedLink?.id;
      if (linkId && this.cytoscapeService.cy) {
        const cyEdge = this.cytoscapeService.cy.$('#' + linkId);
        if (cyEdge && cyEdge.length) {
          const edgeData = cyEdge.data();
          // Update focusedLink with current cytoscape data and patch the form
          const updatedLink = {...this.focusedLink!, ...edgeData};
          this.focusedLink = updatedLink;
          // Reset hasAddedNewProperty to allow patchForm to update metadata
          hasAddedNewProperty.set(false);
          this.patchForm(updatedLink);
          this.cdr.detectChanges();
        }
      }
    });

    setTimeout(() => {
      this.sectionsCount = DomUtils.getElementCount('linkStyleSection');
      this.cdr.detectChanges();
    }, 200);
  }

  ngOnDestroy(): void {
    // Unsubscribe from undo/redo events
    this.undoRedoSubscription?.unsubscribe();
    this.styleTouchSubscription?.unsubscribe();

    // Skip update if switching to node selection
    if (DashboardComponent.tappedNode) {
      return;
    }

    // Skip update if undo/redo just happened to prevent re-applying undone changes
    if (undoRedoJustHappened()) {
      return;
    }

    // Skip update if this link was part of a recent multi-selection metadata change
    if (this.focusedLink?.id && lastMultiSelectionMetadataIds().has(this.focusedLink.id)) {
      return;
    }

    if (!updateInProgress()) {
      // Check if the edge is currently being edited (anchor points being manipulated)
      if (this.focusedLink?.id) {
        const cyEdge = this.cytoscapeService.cy?.getElementById(this.focusedLink.id);
        const isEditing = cyEdge?.hasClass('cy-edge-editing-highlight');

        // Only update if not currently editing anchor points
        if (!isEditing) {
          this.update(true);
        }
      }
    }
  }

  patchForm(model: GraphNodeLink): void {
    this.isPatchingForm = true;
    this.formGroup.patchValue({
      name: model.name,
      description: model.description,
      label: model.label,
      // Explicitly convert weight to string to ensure proper handling with displayMultiplier
      weight: String((model?.weight === undefined || model?.weight === null) ? DefaultValues.link.weight : model.weight),
      type: model.type,
      shape: model.shape,
      textFontSize: model?.textFontSize || DefaultValues.link.textFontSize,
      color: model.color,
      direction: model.direction,
      excludedFromState: model.excludedFromState ? 'true' : 'false',
      edgeArrowType: model.edgeArrowType || DefaultValues.link.edgeArrowType,
      soundUrl: model.soundUrl || DefaultValues.link.soundUrl,
      labelSlope: model.labelSlope || DefaultValues.link.labelSlope,
      linkLabelPosition: model.linkLabelPosition ?? 0,
      linkLabelOffset: model.linkLabelOffset ?? DefaultValues.link.linkLabelOffset
    });

    // Skip metadata patching if user just added a new property row - prevents focus loss
    if (!hasAddedNewProperty()) {
      if (model.metadata !== undefined) {
        this.metadataControl.setValue(orderBy(visibleMetadata(model.metadata), ['order'], ['asc']));
      } else if (model.metadata === null || model.metadata === undefined) {
        this.metadataControl.setValue([]);
      }
    }

    setTimeout(() => this.isPatchingForm = false, 500);
  }

  /**
   * Name field gained focus: start (or resume) a focus session so the first
   * edit made from here on is pushed as its own undo entry, capturing the
   * value the field had *before* any of this session's edits.
   */
  onNameFieldFocus() {
    if (!this.focusedLink?.id) return;
    const currentValue = this.cytoscapeService.cy
      ?.getElementById(this.focusedLink.id)
      ?.data('name') ?? this.focusedLink.name ?? '';
    this.focusSessionService.startSession(this.focusedLink.id, 'link', 'name', currentValue);
  }

  onInstantNameChange(name: string) {
    if (!this.focusedLink?.id || !this.cytoscapeService.cy) return;
    const cyEdge = this.cytoscapeService.cy.getElementById(this.focusedLink.id);
    if (!cyEdge || !cyEdge.length) return;
    const stateData = this.cytoscapeService.getLinkDataBasedOnState(cyEdge as any);
    if (stateData) {
      stateData.name = name;
    }
    cyEdge.data('name', name);
    this.cytoscapeService.cy.style().update();

    // A brand-new link that has never lost focus yet: naming it is part of
    // the creation action itself, not a separate undo entry (MAP-1547).
    if (!this.focusedLink.isNew) {
      const session = this.focusSessionService.getCurrentSession();
      if (
        session &&
        session.entityId === this.focusedLink.id &&
        session.entityType === 'link' &&
        session.field === 'name' &&
        name !== session.startValue
      ) {
        const textCommand = this.commandFactory.createTextChangeCommand(
          this.focusedLink.id,
          'link',
          'name',
          session.startValue,
          name,
          session.id,
        );
        this.undoRedoManager.pushCommand(textCommand);
      }
    }
  }

  onTitleDescriptionChange(event: { title: string, description?: string }): void {
    this.formGroup.patchValue({
      name: event.title,
      description: event.description
    });
  }

  onNameFieldBlur() {
    if (this.focusedLink && this.focusedLink.id && this.cytoscapeService.cy) {
      this.focusedLink.isNew = false;
      const cyElement = this.cytoscapeService.cy.getElementById(this.focusedLink.id);
      if (cyElement.length > 0) {
        cyElement.removeData('isNew');
      }
    }
    // Freeze the name-change entry: further edits (e.g. after reselecting)
    // start a new focus session and land on their own undo entry.
    this.focusSessionService.endSession(this.formGroup.get('name')?.value ?? '');
  }

  onDescriptionFieldFocus() {
    if (!this.focusedLink?.id) return;
    const currentValue = this.cytoscapeService.cy
      ?.getElementById(this.focusedLink.id)
      ?.data('description') ?? this.focusedLink.description ?? '';
    this.focusSessionService.startSession(this.focusedLink.id, 'link', 'description', currentValue);
  }

  onDescriptionFieldBlur() {
    this.focusSessionService.endSession(this.formGroup.get('description')?.value ?? '');
  }

  resetState(): void {
    if (this.focusedLink) {
      this.resetEvent.emit({ link: this.focusedLink });
    }
  }

  selectNameInput(): void {
    setTimeout(() => {
      this.linkNameInput?.nativeElement?.select();
    }, 100);
  }

  update(onDestroy = false): void {
    if (!this.focusedLink) return;
    const raw = this.formGroup.getRawValue();
    const metadataFormData = this.formGroup.get('metadata')?.getRawValue()?.pairs || this.formGroup.get('metadata')?.getRawValue();

    // Get current anchor point data from the cytoscape element, not from focusedLink
    // This ensures we save the latest anchor positions after the user has dragged handles
    const cyEdge = this.focusedLink.id ? this.cytoscapeService.cy?.getElementById(this.focusedLink.id) : null;
    const currentBendPointPositions = cyEdge?.data('bendPointPositions') || this.focusedLink?.bendPointPositions;
    const currentControlPointPositions = cyEdge?.data('controlPointPositions') || this.focusedLink?.controlPointPositions;
    const currentBendWeights = cyEdge?.data('cyedgebendeditingWeights') || this.focusedLink?.cyedgebendeditingWeights;
    const currentBendDistances = cyEdge?.data('cyedgebendeditingDistances') || this.focusedLink?.cyedgebendeditingDistances;
    const currentControlWeights = cyEdge?.data('cyedgecontroleditingWeights') || this.focusedLink?.cyedgecontroleditingWeights;
    const currentControlDistances = cyEdge?.data('cyedgecontroleditingDistances') || this.focusedLink?.cyedgecontroleditingDistances;

    const link: Partial<GraphNodeLink> = {
      id: this.focusedLink?.id || '',
      name: raw.name || '',
      description: raw.description || '',
      label: raw.label || DefaultValues.link.label,
      // Ensure weight is correctly parsed and not accidentally divided by displayMultiplier
      weight: parseFloat(raw.weight as string),
      color: raw.color || '',
      textFontSize: raw.textFontSize || '0',
      excludedFromState: raw.excludedFromState === 'true',
      type: raw.type || '',
      shape: raw.shape || '',
      direction: raw.direction || DefaultValues.link.direction,
      edgeArrowType: raw.edgeArrowType || DefaultValues.link.edgeArrowType,
      soundUrl: raw.soundUrl?.trim() || null,
      labelSlope: raw.labelSlope || DefaultValues.link.labelSlope,
      linkLabelPosition: raw.linkLabelPosition ?? 0,
      linkLabelOffset: raw.linkLabelOffset ?? DefaultValues.link.linkLabelOffset,
      states: this.focusedLink.states,
      bendPointPositions: currentBendPointPositions,
      controlPointPositions: currentControlPointPositions,
      cyedgebendeditingWeights: currentBendWeights,
      cyedgebendeditingDistances: currentBendDistances,
      cyedgecontroleditingWeights: currentControlWeights,
      cyedgecontroleditingDistances: currentControlDistances,
      metadata: metadataFormData.map((obj: any) => {
        if (obj.id === '') {
          const { id, ...rest } = obj;
          return rest;
        } else {
          return obj;
        }
      }).filter((obj: any) => (obj.key && obj.key.trim() !== '') || (obj.value && obj.value.trim() !== ''))
        // system metadata never passes through the form control — carry it over
        .concat(systemMetadata(this.focusedLink.metadata))
    };
    this.focusedLink = { ...this.focusedLink, ...link };
    this.focusedLink.metadata = orderBy(this.focusedLink.metadata ? [...this.focusedLink.metadata] : [], ['order'], ['asc']);

    link.metadata = this.focusedLink.metadata;
    if (onDestroy) {
      set(link, 'shouldUpdate', true);
    }
    this.updateLinkEvent.emit(link);
  }

  get hasBottleneckMark(): boolean {
    return !!this.focusedLink?.metadata?.some(m => m.key === BOTTLENECK_KEY);
  }

  get visibleMetaCount(): number {
    return visibleMetadata(this.focusedLink?.metadata).length;
  }

  onBottleneckChange(metadata: MetaData[]): void {
    if (!this.focusedLink?.id) return;
    this.focusedLink = {...this.focusedLink, metadata};
    this.updateLinkEvent.emit({id: this.focusedLink.id, metadata} as Partial<ExtendedGraphNodeLink>);
  }

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;

  constructor(private cdr: ChangeDetectorRef,
              private mapService: MapService,
              private cytoscapeService: CytoscapeService,
              private packageService: PackageService,
              private el: ElementRef,
              private focusSessionService: FocusSessionService,
              private undoRedoManager: UndoRedoManagerService,
              private commandFactory: CommandFactoryService) {
    super();
    this.currentMap = this.mapService.getCurrentSelectedMapFromStore();
  }

  get isDefaulter(): boolean {
    return this.packageService.isDefaulter();
  }
}

interface ExtendedGraphNodeLink extends GraphNodeLink {
  isNew?: boolean;
}
